import React from 'react';
import { format } from 'date-fns';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';

import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import Button from '@dashboard/components/Button';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Callout from '@dashboard/components/Callout';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import PropertyList from '@dashboard/components/PropertyList';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

const SUBSCRIPTION_PAYMENT_STATUS_LABELS = {
  succeeded: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  incomplete: 'Incomplete',
};

function getSubscriptionPaymentStatusLabel(paid, paymentStatus) {
  if (paid) return 'Paid';

  return SUBSCRIPTION_PAYMENT_STATUS_LABELS[paymentStatus];
}

const PaymentsSubscriptionsDetailPayments = ({ subscription, subscriptionId }) => {
  const { token, seller, isAdminLoggedInAsSeller } = useAuth();

  const {
    data: subscriptionPayments,
    error: subscriptionPaymentsError,
    isLoading: isLoadingSubscriptionPayments,
  } = useQuery({
    queryKey: ['subscriptionPayments', subscriptionId],
    queryFn: () =>
      fetcher(`/sellers/${subscription.seller}/subscriptions/${subscriptionId}/payments`, token),
  });

  return (
    <div className="flex flex-col lg:flex-row mx-auto items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8">
        <Card className="mx-auto !px-0 !py-2">
          <CardContent>
            <PropertyList.Title>Payments</PropertyList.Title>

            {isLoadingSubscriptionPayments && <Spinner />}

            {subscriptionPaymentsError && (
              <Callout variant="error">Failed to load subscription payments</Callout>
            )}

            {subscriptionPayments?.length > 0 && (
              <div className="overflow-auto">
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="medium" className="!pl-0">
                    Amount
                  </CardListItemElement>
                  <CardListItemElement size="xl">Date</CardListItemElement>
                  <CardListItemElement align="right" size="medium">
                    Status
                  </CardListItemElement>
                  <CardListItemElement align="right" size="2xl">
                    Invoice
                  </CardListItemElement>
                  <CardListItemElement
                    align="right"
                    size="large"
                    className="!pr-0"
                  ></CardListItemElement>
                </CardListItem>

                {subscriptionPayments.map(payment => (
                  <CardListItem key={payment._id}>
                    <CardListItemElement size="medium" className="!pl-0">
                      {getPriceLabel({
                        amount: payment.amount,
                        currency: subscription.currency,
                      })}
                    </CardListItemElement>
                    <CardListItemElement size="xl">
                      {format(new Date(payment.createdAt), 'd MMM, yyyy, HH:mm')}
                    </CardListItemElement>
                    <CardListItemElement align="right" size="medium">
                      <StatusBadge
                        status={payment.paid ? 'paid' : payment.paymentStatus}
                        label={getSubscriptionPaymentStatusLabel(
                          payment.paid,
                          payment.paymentStatus,
                        )}
                      />
                    </CardListItemElement>
                    <CardListItemElement align="right" size="2xl" className="!pr-0">
                      <div className="flex gap-x-2">
                        <Button
                          appearance="secondary"
                          size="small"
                          href={payment.stripeInvoicePdf}
                          blank
                        >
                          Download PDF
                        </Button>

                        <div className="flex-0">
                          <CopyTextWithTooltip
                            text={payment.stripeInvoicePdf}
                            tooltipText="Copy invoice URL"
                          >
                            <div>
                              <Button appearance="secondary" size="small">
                                Copy link
                              </Button>
                            </div>
                          </CopyTextWithTooltip>
                        </div>
                      </div>
                    </CardListItemElement>
                    <CardListItemElement align="right" size="large" className="!pr-0">
                      <Button
                        appearance="secondary"
                        size="small"
                        href={getStripeDashboardUrl(
                          `/payments/${payment.stripePaymentId || payment.stripePaymentIntentId}`,
                          {
                            seller,
                            isAdminLoggedInAsSeller,
                          },
                        )}
                        blank
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" /> View in Stripe
                      </Button>
                    </CardListItemElement>
                  </CardListItem>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsSubscriptionsDetailPayments;
